import { TriangleAlert } from "lucide-react"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { useTranslation } from "react-i18next"
import {
  PreferenceError,
  PreferenceId,
  PriorityId,
  SubjectId,
} from "./store/types"
import { usePageSelector } from "./store"
import { subjectSelectors } from "./store/subjects-slice"
import { preferencesSelectors } from "./store/preferences-slice"
import { prioritiesSelectors } from "./store/priorities-slice"

export function Errors({ errors }: { errors: Array<PreferenceError> }) {
  if (errors.length === 0) return

  return (
    <Alert variant={"destructive"} className="p-2">
      <Accordion type="multiple">
        {errors.map((error) => {
          switch (error.kind) {
            case "conflict":
              return <ConflictError key={error.kind} {...error} />
            case "studentIneligible":
              return <EligibilityError key={error.kind} {...error} />
            case "invalidClassDocument":
              return <InvalidClassError key={error.kind} {...error} />
          }
        })}
      </Accordion>
    </Alert>
  )
}

function SubjectConflict({
  subjectId,
  conflictsCount,
  children,
}: {
  subjectId: SubjectId
  conflictsCount: number
  children: React.ReactNode
}) {
  const { name } = usePageSelector((state) =>
    subjectSelectors.selectSubject(state, subjectId),
  )
  return (
    <li key={subjectId} className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <span className="text-destructive font-medium">{name}</span>
        <Badge variant="destructive">{conflictsCount}</Badge>
      </div>
      {children}
    </li>
  )
}

function Conflict({
  preferenceId,
  priorityId,
}: {
  preferenceId: PreferenceId
  priorityId: PriorityId
}) {
  const { rank: preference } = usePageSelector((state) =>
    preferencesSelectors.selectPreference(state, preferenceId),
  )
  const { priority } = usePageSelector((state) =>
    prioritiesSelectors.selectPriority(state, priorityId),
  )
  const { t } = useTranslation("preferences")

  return (
    <li className="flex items-center gap-2">
      <Badge variant="destructive">
        {t("timetable.events.short_title", {
          preference,
          priority,
        })}
      </Badge>
    </li>
  )
}

function ConflictError({
  conflicts,
}: Extract<PreferenceError, { kind: "conflict" }>) {
  const { t } = useTranslation("preferences")
  if (conflicts.length === 0) return null

  return (
    <AccordionItem value="conflicts">
      <AccordionTrigger className="p-2">
        <AlertTitle className="inline-flex flex-row items-center gap-2 text-sm font-semibold">
          <TriangleAlert className="size-4" />
          {t("errors.conflicts.title")}
        </AlertTitle>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4">
        <AlertDescription>{t("errors.conflicts.description")}</AlertDescription>
        <ul className="flex flex-col gap-3">
          {conflicts.map(([subjectId, pairs]) => (
            <SubjectConflict
              subjectId={subjectId}
              conflictsCount={pairs.length}
            >
              <ul className="border-destructive/25 ml-1 flex flex-col gap-1.5 border-l pl-3">
                {pairs.map(([priorityId, preferenceId], index) => (
                  <Conflict
                    key={index}
                    preferenceId={preferenceId}
                    priorityId={priorityId}
                  />
                ))}
              </ul>
            </SubjectConflict>
          ))}
        </ul>
      </AccordionContent>
    </AccordionItem>
  )
}

function EligibilityError({
  preferences,
}: Extract<PreferenceError, { kind: "studentIneligible" }>) {
  const { t } = useTranslation("preferences")
  if (preferences.length === 0) return null

  return (
    <AccordionItem value="eligibility">
      <AccordionTrigger className="p-2">
        <AlertTitle className="inline-flex flex-row items-center gap-2 text-sm font-semibold">
          <TriangleAlert className="size-4" />
          {t("errors.eligibility.title")}
        </AlertTitle>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4">
        <AlertDescription>
          {t("errors.eligibility.description")}
        </AlertDescription>
      </AccordionContent>
    </AccordionItem>
  )
}

function InvalidClassError({
  preferences,
}: Extract<PreferenceError, { kind: "invalidClassDocument" }>) {
  const { t } = useTranslation("preferences")
  if (preferences.length === 0) return null

  return (
    <AccordionItem value="eligibility">
      <AccordionTrigger className="p-2">
        <AlertTitle className="inline-flex flex-row items-center gap-2 text-sm font-semibold">
          <TriangleAlert className="size-4" />
          {t("errors.invalid.title")}
        </AlertTitle>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4">
        <AlertDescription>{t("errors.invalid.description")}</AlertDescription>
      </AccordionContent>
    </AccordionItem>
  )
}
